import type { Plan } from '@/types';
import { BRAND } from '@/lib/brand';

type CellValue = boolean | string;

/**
 * Feature matrix rendered as the comparison table at the bottom of
 * /plans. V2 collapses the prior 4-column (Free/Starter/Pro/Enterprise)
 * display into 3 columns (Starter/Pro/Scale) — Free is hidden from the
 * pricing page entirely (users only land on Free via cancellation;
 * cards there compare paid plans only). Mirrors the website's
 * PricingCards.tsx feature bullets.
 */
export interface MatrixRow {
  feature: string;
  free: CellValue;
  starter: CellValue;
  pro: CellValue;
  scale: CellValue;
  group?: string;
  groupDescription?: string;
}

export const FEATURE_MATRIX: MatrixRow[] = [
  {
    feature: 'Enrichment from 15+ providers',
    free: true,
    starter: true,
    pro: true,
    scale: true,
    group: 'Data enrichment',
    groupDescription: 'Find and enrich data from 15+ providers',
  },
  { feature: 'AI Search', free: false, starter: true, pro: true, scale: true },
  {
    feature: 'Chrome Extension',
    free: true,
    starter: true,
    pro: true,
    scale: true,
  },
  { feature: 'CSV Upload', free: false, starter: true, pro: true, scale: true },
  {
    feature: 'Manual Entry',
    free: true,
    starter: true,
    pro: true,
    scale: true,
  },
  { feature: 'Export CSV', free: false, starter: true, pro: true, scale: true },
  {
    feature: 'Credit Rollover',
    free: true,
    starter: true,
    pro: true,
    scale: true,
  },

  {
    feature: 'Find People',
    free: false,
    starter: true,
    pro: true,
    scale: true,
    group: 'Prospecting',
    groupDescription: 'Find and manage leads at scale',
  },
  {
    feature: 'Sales Navigator Sync',
    free: false,
    starter: true,
    pro: true,
    scale: true,
  },
  {
    feature: 'Leads per list',
    free: '100',
    starter: '2,500',
    pro: '2,500',
    scale: 'Unlimited',
  },

  {
    feature: 'Included seats',
    free: '1',
    starter: '2',
    pro: '5',
    scale: '10',
    group: 'Team',
    groupDescription: 'Add more seats anytime ($20/seat/month)',
  },
  {
    feature: 'Additional seats',
    free: '—',
    starter: '$20/seat/mo',
    pro: '$20/seat/mo',
    scale: '$20/seat/mo',
  },

  {
    feature: 'AI Agents (Basic)',
    free: false,
    starter: true,
    pro: true,
    scale: true,
    group: 'Automation & AI',
    groupDescription: 'Automate workflows with AI-powered columns',
  },
  {
    feature: 'AI Agents (Advanced)',
    free: false,
    starter: false,
    pro: false,
    scale: true,
  },
  {
    feature: 'Playbooks',
    free: false,
    starter: false,
    pro: false,
    scale: true,
  },

  {
    // Starter+, unlike the rest of this group. Gated by mcp_access_allowed()
    // server-side, which is deliberately NOT the API_ACCESS check below.
    feature: 'MCP Access',
    free: false,
    starter: true,
    pro: true,
    scale: true,
    group: 'Integrations',
    groupDescription: `Connect ${BRAND.name} to your tech stack`,
  },
  {
    feature: 'CRM Import',
    free: false,
    starter: false,
    pro: true,
    scale: true,
  },
  {
    feature: 'Native CRM Sync',
    free: false,
    starter: false,
    pro: true,
    scale: true,
  },
  {
    feature: 'API Access & Webhooks',
    free: false,
    starter: false,
    pro: true,
    scale: true,
  },

  {
    feature: 'Priority Support',
    free: false,
    starter: false,
    pro: true,
    scale: true,
    group: 'Support & Analytics',
    groupDescription: 'Get the help and insights you need',
  },
  {
    feature: 'Analytics Dashboard',
    free: false,
    starter: false,
    pro: false,
    scale: true,
  },
  {
    feature: 'Volume Pricing Rates',
    free: false,
    starter: false,
    pro: false,
    scale: true,
  },
];

/**
 * V2 tier groups rendered as the 3 pricing cards. Legacy `enterprise`
 * is intentionally not a category here.
 */
export interface TierGroup {
  category: 'starter' | 'pro' | 'scale';
  label: string;
  description: string;
  highlights: string[];
  baseSeats: number;
  previousTier: string | null;
  plans: Plan[];
  isPopular: boolean;
}

export function groupPlansByTier(plans: Plan[]): TierGroup[] {
  const groups: TierGroup[] = [
    {
      category: 'starter',
      label: 'Starter',
      description: 'For individuals and small teams getting started',
      highlights: [
        '2 seats included (+$20/mo per extra seat)',
        'AI Search',
        'Find People',
        'MCP Access',
        'Sales Navigator Sync',
        'Export CSV',
      ],
      baseSeats: 2,
      previousTier: null,
      plans: [],
      isPopular: false,
    },
    {
      category: 'pro',
      label: 'Pro',
      description: 'For teams scaling their prospecting workflows',
      highlights: [
        '5 seats included (+$20/mo per extra seat)',
        'CRM Import & Integrations',
        'API Access & Webhooks',
        'Priority Support',
      ],
      baseSeats: 5,
      previousTier: 'Starter',
      plans: [],
      isPopular: true,
    },
    {
      category: 'scale',
      label: 'Enterprise',
      description: 'For growth teams running heavy enrichment at scale',
      highlights: [
        '10 seats included (+$20/mo per extra seat)',
        'Playbooks',
        'AI Agents (Advanced)',
        'Analytics Dashboard',
      ],
      baseSeats: 10,
      previousTier: 'Pro',
      plans: [],
      isPopular: false,
    },
  ];

  const groupsByCategory = new Map(groups.map((g) => [g.category, g]));

  for (const plan of plans) {
    // Prefer the backend-provided `tier_key` field once the V2 plans API
    // ships; fall back to name matching for plans that don't carry it.
    // Word-boundary matching avoids "Cleanlist Starter Pro" accidentally
    // landing in the pro bucket.
    const tierKey = plan.tier_key;
    let category: TierGroup['category'] | null = null;
    if (tierKey === 'starter' || tierKey === 'pro' || tierKey === 'scale') {
      category = tierKey;
    } else {
      const lower = plan.name.toLowerCase();
      if (/\bscale\b/.test(lower)) category = 'scale';
      else if (/\bpro\b/.test(lower)) category = 'pro';
      else if (/\bstarter\b/.test(lower)) category = 'starter';
    }
    if (category) {
      groupsByCategory.get(category)?.plans.push(plan);
    }
    // Free + legacy enterprise plans intentionally drop here — the
    // page renders them via the legacy banner header instead.
  }

  for (const g of groups) {
    g.plans.sort(
      (a, b) =>
        (a.monthly_price?.price ?? a.yearly_price?.price ?? 0) -
        (b.monthly_price?.price ?? b.yearly_price?.price ?? 0)
    );
  }

  // Always return 3 — empty plan arrays render a skeleton card so the
  // grid layout stays stable while plan data loads.
  return groups;
}
